// Axios.post('https://bankvalidation.rightio.co.uk/BankValidation.asmx?op=GetBankDetails',
import Axios, { AxiosRequestConfig, AxiosResponse } from "axios";
import { store } from "../../../../../../../../../store";
import { logger } from "@services/workers/helpers";
import memoization from "@services/memoization";
import { TemplateUIObject } from "@services/storage/storeTemplate";
import { serviceAgreement } from "@src/utilities/objects";
import { ui, state } from "@src/interfaces/interfaces";

const namespace = "ServiceAgreementPaymentDirectDebitForm";

// AddressSearch

export interface IBankCheck {
  (data: string, operation: string, config: AxiosRequestConfig): Promise<AxiosResponse<string>>;
}

export const BankCheck: IBankCheck = async (b, c, d) => {
  const reply = await Axios.post(`https://bankvalidation.rightio.co.uk/BankValidation.asmx?op=${c}`, b, d);
  return reply;
};

export const xmlToObject = (xml: string) => {
  // instantiate the parser
  const parser = new DOMParser();

  // parse the xml string
  const xmlDoc = parser.parseFromString(xml, "text/xml");

  // get all the nodes names
  const nodes = xmlDoc.getElementsByTagName("*");

  // create an object to store the data
  const data: any = {};

  // loop through the nodes
  for (let i = 0; i < nodes.length; i++) {
    // get the node
    const node = nodes[i];

    // get the node name
    const nodeName = node.nodeName;

    // ignore nodes starting with "soap" as they are not needed, ignore nodes starting with "GetBankDetailsResult" as they are not needed, ignore nodes starting with "GetBankDetails" as they are not needed
    if (
      nodeName.startsWith("soap") ||
      nodeName.startsWith("GetBankDetailsResult") ||
      nodeName.startsWith("GetBankDetails")
    ) {
      continue;
    }

    // get the node value
    const nodeValue = node.textContent;

    // check if value can be parsed to a boolean
    if (nodeValue === "true" || nodeValue === "false") {
      // assign the node value to the node name
      data[nodeName] = nodeValue === "true";
    } else {
      // assign the node value to the node name
      data[nodeName] = nodeValue;
    }
  }

  // return the data
  return data;
};

interface PrepareXMLS {
  (accountNumber: number | string, sortCode: number | string): string;
}

export const prepareXmls: PrepareXMLS = (accountNumber, sortCode) => `<?xml version="1.0" encoding="utf-8"?>
<soap12:Envelope xmlns:xsi="http://www.w3.org/2001/XMLSchema-instance" xmlns:xsd="http://www.w3.org/2001/XMLSchema" xmlns:soap12="http://www.w3.org/2003/05/soap-envelope">
  <soap12:Body>
    <GetBankDetails xmlns="Rightio">
      <accountNumber>${accountNumber}</accountNumber>
      <sortCode>${sortCode}</sortCode>
    </GetBankDetails>
  </soap12:Body>
</soap12:Envelope>`;

const nullServiceAgreement = () => {
  // null service agreement payment details
  serviceAgreement.serviceAgreementPaymentDetails.directDebitBankName = undefined;
  serviceAgreement.serviceAgreementPaymentDetails.directDebitBankPostcode = undefined;
  serviceAgreement.serviceAgreementPaymentDetails.directDebitBankTown = undefined;
  serviceAgreement.serviceAgreementPaymentDetails.directDebitBankCounty = undefined;
  serviceAgreement.serviceAgreementPaymentDetails.directDebitBankProperty = undefined;
  serviceAgreement.serviceAgreementPaymentDetails.directDebitBankStreet = undefined;
  serviceAgreement.serviceAgreementPaymentDetails.directDebitBankLocality = undefined;
  serviceAgreement.serviceAgreementPaymentDetails.stripePaymentMethodId = undefined;
};

export const processXMLS = (
  xmls: any,
  ui: TemplateUIObject,
  prefix: "CarePlanPaymentBankDetails",
  setSucceeded: (value: boolean) => void,
  setError: (value: string) => void
) => {
  // get data from xml
  const data = xmlToObject(xmls);

  nullServiceAgreement();

  // set bank details on service agreement hmmm should be in middleware

  // set bank details on service agreement if response is valid, hmm again some should be in middleware
  if (data?.IsValid) {
    data?.BankName &&
      (ui[`${prefix}BankName`] = data.BankName) &&
      (serviceAgreement.serviceAgreementPaymentDetails.directDebitBankName = data.BankName);
    data?.BranchPostcode &&
      (serviceAgreement.serviceAgreementPaymentDetails.directDebitBankPostcode = data.BranchPostcode);
    data?.BranchTown && (serviceAgreement.serviceAgreementPaymentDetails.directDebitBankTown = data.BranchTown);
    data?.BranchCounty && (serviceAgreement.serviceAgreementPaymentDetails.directDebitBankCounty = data.BranchCounty);
    data?.BranchAddress1 &&
      (serviceAgreement.serviceAgreementPaymentDetails.directDebitBankProperty = data.BranchAddress1);
    data?.BranchAddress2 &&
      (serviceAgreement.serviceAgreementPaymentDetails.directDebitBankStreet = data.BranchAddress2);
    data?.BranchAddress3 &&
      (serviceAgreement.serviceAgreementPaymentDetails.directDebitBankLocality = data.BranchAddress3);
    data?.BranchName && (ui[`${prefix}BranchName`] = data.BranchName);
    setSucceeded(true);
  } else {
    ui[`${prefix}BankName`] = "";
    ui[`${prefix}BranchName`] = "";
    setSucceeded(false);
    setError(data?.ErrorMessage);
    logger.error(namespace + "->" + processXMLS.name, data?.ErrorMessage);
  }

  //below should be moved to contact / address forms ??
  serviceAgreement.serviceAgreementPaymentDetails.isEncrypted = false;
  serviceAgreement.serviceAgreementPaymentDetails.serviceAgreementBillingFrequencyId =
    ui.CarePlanPlanDetailsPlanOptionsBillingFrequency?.serviceAgreementBillingFrequencyId ?? 0;
  serviceAgreement.serviceAgreementPaymentDetails.serviceAgreementPaymentTypeId =
    ui.CarePlanBillingMethodPaymentType?.serviceAgreementPaymentTypeId ?? 0;
  serviceAgreement.serviceAgreement.serviceAgreementStatusId = 1; // active
};

export const getBankDetails = (
  accountNumber: string | number,
  sortCode: string | number,
  ui: TemplateUIObject,
  prefix: "CarePlanPaymentBankDetails",
  setSucceeded: (value: boolean) => void,
  setError: (value: any) => void
) => {
  ui.CarePlanPaymentBankDetailsBankName = "";
  ui.CarePlanPaymentBankDetailsBranchName = "";

  store.dispatch({ type: "SET_UI_STATE", payload: true, name: "Loading", error: "", body: null });

  const inputData = prepareXmls(accountNumber, sortCode);
  const operation = "GetBankDetails";

  const memoizedCallName = `${operation}-${accountNumber}-${sortCode}`;

  const callback = () => BankCheck(inputData, operation, { headers: { "Content-Type": "text/xml" } });

  memoization
    .get<AxiosResponse<string>>(memoizedCallName, callback)
    .then((res) => {
      processXMLS(res.data, ui, prefix, setSucceeded, setError);
      store.dispatch({ type: "SET_UI_STATE", payload: false, name: "Loading", error: "", body: null });
    })
    .catch((err) => {
      logger.error(namespace + "->" + getBankDetails.name, err);
      setError("Incorrect details entered.");
      ui[`${prefix}BankName`] = "";
      ui[`${prefix}BranchName`] = "";
      store.dispatch({ type: "SET_UI_STATE", payload: false, name: "Loading", error: "", body: null });
    });
};

export const submit =  () => {
  serviceAgreement.submitThrottled(ui, state.UserId, true)
};